import { z } from 'zod';
import type {
    ListTransactionLogPageResponse,
    TransactionLogListItem,
    TransactionLogOperation,
    TransactionLogSearchRequest,
    TransactionLogTablePrimaryKeyFilter,
    TransactionLogTableName,
} from '../transaction-log.types';
import { TransactionLogTableNames } from '../transaction-log.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { AuthorizationPaginationLimitQuerySchema } from './authorization.schemas';
import { JsonValueSchema, NonEmptyStringSchema } from './common.schemas';
import {
    SearchFieldPredicatesSchema,
    SearchJsonPathPredicatesSchema,
    SearchTermsSchema,
} from './omnisearch.schemas';

export const TransactionLogTableNameSchema: z.ZodType<TransactionLogTableName> =
    z.enum(TransactionLogTableNames);

export const TransactionLogOperationSchema: z.ZodType<TransactionLogOperation> = z.enum([
    'INSERT',
    'UPDATE',
    'DELETE',
]);

export const TransactionLogPaginationCursorQuerySchema: z.ZodType<string | undefined> = z
    .preprocess(
        (value: unknown): unknown => {
            if (value === undefined || value === null) {
                return undefined;
            }
            const stringParseResult = z.string().safeParse(value);
            if (!stringParseResult.success) {
                return value;
            }
            const trimmed = stringParseResult.data.trim();
            return trimmed.length === 0 ? undefined : trimmed;
        },
        z.string().min(1).optional()
    )
    .optional();

export const TransactionLogTablePrimaryKeyFilterSchema: z.ZodType<
    TransactionLogTablePrimaryKeyFilter
> = z
    .object({
        table_name: TransactionLogTableNameSchema,
        primary_key_values: z.array(z.string().trim().min(1)).min(1).max(100),
    })
    .strict();

export const TransactionLogSearchRequestSchema: z.ZodType<TransactionLogSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: TransactionLogPaginationCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: SearchJsonPathPredicatesSchema,
        tables: z.array(TransactionLogTableNameSchema).max(25).optional(),
        table_primary_keys: z
            .array(TransactionLogTablePrimaryKeyFilterSchema)
            .max(25)
            .optional(),
    })
    .strict();

export const TransactionLogListItemSchema: z.ZodType<TransactionLogListItem> = z.object({
    transaction_log_ref: NonEmptyStringSchema,
    ts: z.string().datetime(),
    table_name: TransactionLogTableNameSchema,
    operation: TransactionLogOperationSchema,
    table_row: JsonValueSchema,
    ip_address: NonEmptyStringSchema.nullable(),
    user_agent: NonEmptyStringSchema.nullable(),
});

export const ListTransactionLogPageResponseSchema: z.ZodType<ListTransactionLogPageResponse> =
    z.object({
        transaction_log: z.array(TransactionLogListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
    });
